// Amazon Custom's fontsCssUrl is a plain CSS file with one @font-face block per family,
// e.g.: @font-face { font-family: 'Abel'; src: url('...woff2') format('woff2'), url('...ttf') format('truetype'), ... }
// The backend font upload endpoint only accepts .ttf/.otf, so we specifically pull the
// format('truetype') url out of each block.
const FONT_FACE_BLOCK_REGEX = /@font-face\s*{([^}]*)}/g;
const FONT_FAMILY_REGEX = /font-family:\s*['"]([^'"]+)['"]/;
const TTF_SRC_REGEX = /url\(['"]([^'"]+\.ttf)['"]\)\s*format\(['"]truetype['"]\)/;

export const parseFontFaceCss = (css: string): Map<string, string> => {
    const familyToTtfUrl = new Map<string, string>();
    // FONT_FACE_BLOCK_REGEX is a module-level /g regex, so its lastIndex persists across calls;
    // reset it here so a previous (possibly aborted) parse never skews this one.
    FONT_FACE_BLOCK_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = FONT_FACE_BLOCK_REGEX.exec(css))) {
        const block = match[1];
        const family = FONT_FAMILY_REGEX.exec(block)?.[1];
        const ttfUrl = TTF_SRC_REGEX.exec(block)?.[1];
        if (family && ttfUrl) {
            familyToTtfUrl.set(family, ttfUrl);
        }
        // Guard against zero-length matches, which would leave lastIndex unchanged and loop forever.
        if (match.index === FONT_FACE_BLOCK_REGEX.lastIndex) {
            FONT_FACE_BLOCK_REGEX.lastIndex++;
        }
    }

    return familyToTtfUrl;
};

export const fetchFontFaceMap = async (cssUrl: string): Promise<Map<string, string>> => {
    const response = await fetch(cssUrl);
    if (!response.ok) {
        throw new Error(`Failed to fetch font CSS (${response.status}): ${cssUrl}`);
    }
    return parseFontFaceCss(await response.text());
};

export const downloadFontFile = async (url: string, fileName: string): Promise<File> => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to download font file (${response.status}): ${url}`);
    }
    const blob = await response.blob();
    return new File([blob], fileName, { type: 'application/octet-stream' });
};
